/**
 * studioConfigSchema.js
 * ---------------------
 * Single source of truth for the new inline configuration UX (`/studio/preview/{id}?mode=editor`).
 *
 * - BLOCKS = the original 8-module tunnel (unchanged contract — the PDF / récap continues to
 *   walk this structure). DO NOT remove or rename existing question IDs; new questions for the
 *   contextual banners are appended where they belong.
 *
 * - SECTION_BANNERS = for each sandbox `view`, the ordered list of banner question IDs to
 *   surface as the user navigates there. Each entry references a question that already exists
 *   in BLOCKS (or is declared here as a new question added to the appropriate block).
 *
 * - VISUAL_EFFECTS = pure-function helpers that map a question + chosen answer to a visual
 *   change in the sandbox (e.g. "51+ PS" → showTeamBlock=true).
 *
 * Keep this file dependency-free (no React, no axios) so it can be imported from both the
 * Studio Wizard (Studio.jsx) and the live sandbox (StudioPreview.jsx).
 */

import {
  ShieldCheck, UserSquare2, BookOpenCheck, CalendarClock,
  MonitorPlay, UsersRound, FileSignature, Receipt,
} from "lucide-react";

// =============================================================================
// BLOCKS — extended from Studio.jsx (NEW questions appended to existing blocks)
// =============================================================================
export const BLOCKS = {
  IDM: {
    icon: ShieldCheck, title: "Identification & Auth des PS",
    summary: "Authentification forte via Pro Santé Connect + référencement RPPS / FINESS.",
    questions: [
      { id: "psc_cards", type: "radio", label: "Vos PS disposent-ils tous d'une carte CPx ou e-CPS ?",
        helper: "La CPx (carte CPS physique) ou e-CPS (carte dématérialisée) permet l'authentification forte imposée par la réglementation e-santé.",
        options: ["Oui, tous", "Partiellement", "Non / à acquérir", "Je ne sais pas"] },
      { id: "structure_type", type: "checkbox", label: "Type de structure(s) à enregistrer",
        options: ["CPTS", "MSP", "Cabinet libéral", "Clinique", "EHPAD", "Groupe médical"] },
      { id: "ps_count", type: "radio", label: "Combien de PS utiliseront la plateforme ?",
        options: ["1-5", "6-20", "21-50", "51-200", "200+"] },
      { id: "finess_ror", type: "text", label: "N° FINESS / ROR de votre structure principale",
        helper: "Laissez vide si vous ne l'avez pas — nous le retrouverons ensemble.", optional: true },
    ],
  },
  IDP: {
    icon: UserSquare2, title: "Identification Patient & Dossier",
    summary: "Qualification INS + identitovigilance RNIV + dossier patient.",
    questions: [
      { id: "patient_base", type: "radio", label: "Avez-vous une base patient existante à migrer ?",
        options: ["Oui (CSV/Excel)", "Oui (API LGC)", "Non, on démarre à zéro"] },
      { id: "patient_count", type: "radio", label: "Volume patient estimé en année 1",
        options: ["<500", "500-2000", "2000-10000", "10000+"] },
      { id: "ins_workflow", type: "radio", label: "Comment qualifiez-vous l'INS aujourd'hui ?",
        helper: "L'INS (Identifiant National de Santé) est le numéro unique du patient.",
        options: ["Manuellement", "Via mon LGC actuel", "Pas encore en place"] },
    ],
  },
  BPT: {
    icon: BookOpenCheck, title: "Bonnes pratiques HAS",
    summary: "Checklist pertinence + double consentement + mode dégradé.",
    new: true,
    questions: [
      { id: "has_trained", type: "radio", label: "Vos PS sont-ils formés à la téléconsultation HAS ?",
        options: ["Oui tous", "Partiellement", "Non, à prévoir"] },
      { id: "specialties", type: "textarea",
        label: "Quelles spécialités / actes médicaux concernés en priorité ?" },
    ],
  },
  RDV: {
    icon: CalendarClock, title: "Planification & Rendez-vous",
    summary: "Widget MetaConnect agenda interne + widget embarquable externe.",
    questions: [
      { id: "agenda_tool", type: "radio", label: "Agenda connecté actuellement utilisé ?",
        options: ["Doctolib", "Maiia", "KelDoc", "Clickdoc", "Autre", "Aucun"] },
      { id: "use_widget", type: "checkbox", label: "Widgets MetaConnect à activer",
        options: ["Widget interne (plateforme)", "Widget externe (site internet)", "Synchronisation avec agenda existant"] },
      { id: "site_embed", type: "text", label: "URL du site sur lequel intégrer le widget RDV externe" },
      { id: "online_booking", type: "radio", label: "Prise de RDV en ligne par le patient ?",
        options: ["Libre accès", "Après accord PS", "Uniquement par PS"] },
    ],
  },
  PRE: {
    icon: MonitorPlay, title: "Préparation de session",
    summary: "Salle d'attente LiveKit + check technique + emails patients pré-programmés.",
    questions: [
      { id: "reminder_channel", type: "checkbox", label: "Canaux d'emails / rappels patient activés",
        options: ["Email confirmation", "Email rappel 24h", "Email rappel 2h", "SMS rappel (surcoût)", "Email post-session satisfaction"] },
      { id: "patient_tech_level", type: "radio", label: "Profil technique de vos patients",
        options: ["Très autonome", "Variable", "Faible (seniors, aidants)"] },
    ],
  },
  REA: {
    icon: UsersRound, title: "Réalisation de l'acte",
    summary: "Visio LiveKit chiffrée + chat + dispositifs connectés (télésurveillance).",
    questions: [
      { id: "max_concurrent", type: "radio", label: "Téléconsultations simultanées max prévues",
        options: ["1-5", "6-20", "21-100", "100+"] },
      { id: "advanced_features", type: "checkbox", label: "Fonctions avancées souhaitées",
        options: ["Partage d'écran", "Capture image médicale", "Dispositif connecté (BLE/BP/SpO₂)", "Enregistrement (HDS, surcoût)"] },
      // ----- NEW questions surfaced as contextual banners on the Téléconsultation page -----
      { id: "assisted_consult", type: "radio", label: "Activez-vous la téléconsultation assistée ?",
        helper: "La téléconsultation assistée implique un PS délégué auprès du patient avec matériel connecté.",
        options: ["Oui", "Non", "Je ne sais pas"], banner: true },
      { id: "connected_devices", type: "checkbox", label: "Dispositifs connectés souhaités ?",
        helper: "Sélection multiple — adapte la page \"Mon Matériel\" du sandbox.",
        options: ["Stéthoscope connecté", "ECG", "Dermatoscope", "OCT", "Rétinophotographie", "Oxymètre", "Autre"], banner: true },
    ],
  },
  CCL: {
    icon: FileSignature, title: "Conclusion & Documents",
    summary: "Signature électronique + transmission DMP + MSSanté webmail intégré.",
    questions: [
      { id: "mss_existing", type: "radio", label: "Vos PS disposent-ils déjà d'une adresse MSSanté active ?",
        options: ["Oui tous", "Partiellement", "Non — création via MetaConnect"] },
      { id: "report_template", type: "radio", label: "Modèles de comptes rendus existants ?",
        options: ["Oui (à reproduire)", "Non, à créer ensemble"] },
      { id: "lgc_target", type: "text", label: "LGC / DPI cible pour l'export",
        helper: "Ex : Weda, Hellodoc, Médistory, Crossway… Laissez vide si vous n'en avez pas encore.", optional: true },
    ],
  },
  FAC: {
    icon: Receipt, title: "Compte rendu d'activité & Facturation",
    summary: "Push API vers LGC + droits patient ADRi/CDRi.",
    questions: [
      { id: "billing_target", type: "radio", label: "Mode de facturation cible",
        options: ["Indirect via LGC", "FSE directe (Phase 3)", "Privé hors AMO"] },
      { id: "adri_cdri", type: "radio", label: "Interrogation ADRi/CDRi des droits patient ?",
        helper: "ADRi/CDRi vérifient automatiquement les droits avant consultation.",
        options: ["Oui", "Non"] },
    ],
  },
};

// =============================================================================
// SECTION_BANNERS — sandbox view → ordered banner question references
// =============================================================================
// Each entry is { blockId, qId } pointing into BLOCKS. The runtime iterates over
// this list, skips already-answered ones, and shows the first remaining one as
// a sticky contextual banner above the current page content.
export const SECTION_BANNERS = {
  dashboard: [
    { blockId: "IDM", qId: "ps_count" },
    { blockId: "IDM", qId: "psc_cards" },
    { blockId: "IDM", qId: "structure_type" },
  ],
  telecons: [
    { blockId: "REA", qId: "assisted_consult" },
    { blockId: "REA", qId: "connected_devices" },
    { blockId: "REA", qId: "max_concurrent" },
  ],
  webmail: [
    { blockId: "CCL", qId: "mss_existing" },
  ],
  dossiers: [
    { blockId: "IDP", qId: "patient_base" },
    { blockId: "IDP", qId: "ins_workflow" },
  ],
  agenda: [
    { blockId: "RDV", qId: "agenda_tool" },
    { blockId: "RDV", qId: "online_booking" },
  ],
  // Facturation lives on the "comptes-rendus" + "structure" pages today; banner shows on both.
  "comptes-rendus": [
    { blockId: "FAC", qId: "billing_target" },
    { blockId: "FAC", qId: "adri_cdri" },
  ],
};

// =============================================================================
// VISUAL_EFFECTS — derived visual state for the sandbox
// =============================================================================
// Pure function: given the current `answers` object, returns flags the sandbox
// consumes to alter its rendering (extra sidebar items, warning badges, etc.).
// Keep this side-effect-free; the consumer is responsible for re-rendering.
export function deriveSandboxEffects(answers) {
  const a = answers || {};
  const idm = a.IDM || {};
  const rea = a.REA || {};
  const ccl = a.CCL || {};
  const idp = a.IDP || {};
  const rdv = a.RDV || {};

  // 51+ PS → show team block
  const psCount = idm.ps_count || "";
  const showTeamBlock = psCount === "51-200" || psCount === "200+";

  // CPx "Non" → orange warning on PSC button
  const cpxStatus = idm.psc_cards || "";
  const cpxWarn = cpxStatus === "Non / à acquérir";

  // Assisted consult "Oui" → reveal "Mon Matériel" + "Réseau de proximité" + assist badge
  const assistedConsult = rea.assisted_consult || "";
  const assistedEnabled = assistedConsult === "Oui";

  // Connected devices selected → list them on the Matériel page
  const connectedDevices = Array.isArray(rea.connected_devices) ? rea.connected_devices : [];

  // MSSanté "Non — création via MetaConnect" → warning banner on Webmail page
  const mssStatus = ccl.mss_existing || "";
  const mssMissing = mssStatus === "Non — création via MetaConnect";

  // INS "Pas encore en place" → orange badge on patient records
  const insStatus = idp.ins_workflow || "";
  const insWarn = insStatus === "Pas encore en place";

  // Patient base "Oui (CSV/Excel)" → show "Importer mes patients" button
  const patientBase = idp.patient_base || "";
  const showImportCsv = patientBase === "Oui (CSV/Excel)";

  // Agenda tool Doctolib/Maiia → show "Synchronisation disponible" badge
  const agendaTool = rdv.agenda_tool || "";
  const agendaSyncable = agendaTool === "Doctolib" || agendaTool === "Maiia";

  // Online booking → controls the booking widget mode
  const bookingMode = rdv.online_booking || "Après accord PS";

  // Structure type → adapt welcome label
  const structureTypes = Array.isArray(idm.structure_type) ? idm.structure_type : [];
  const primaryStructure = structureTypes[0] || "";

  return {
    showTeamBlock,
    cpxWarn,
    assistedEnabled,
    connectedDevices,
    mssMissing,
    insWarn,
    showImportCsv,
    agendaSyncable,
    bookingMode,
    primaryStructure,
    structureTypes,
  };
}

// =============================================================================
// Progress helper
// =============================================================================
// Counts how many questions across all BLOCKS have a non-empty answer in the
// given `answers` object. Used by the sticky top bar (e.g. "6 / 24 questions").
export function countAnsweredQuestions(answers) {
  const a = answers || {};
  let total = 0;
  let answered = 0;
  for (const blockId of Object.keys(BLOCKS)) {
    for (const q of BLOCKS[blockId].questions) {
      total += 1;
      const v = (a[blockId] || {})[q.id];
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v) && v.length === 0) continue;
      answered += 1;
    }
  }
  return { answered, total };
}

// Find a question definition in BLOCKS by (blockId, qId). O(N) — fine for ≤30 questions.
export function findQuestion(blockId, qId) {
  const block = BLOCKS[blockId];
  if (!block) return null;
  return block.questions.find((q) => q.id === qId) || null;
}
